import Map, { FullscreenControl, GeolocateControl, Marker, MapRef } from 'react-map-gl';
import { useRef, useState, useCallback, useEffect } from 'react';
import { MarkerData } from '../../../types/markerData';
import MarkerModal from '../modal/MarkerModal';
import './mapBox.css'
import StylesControl from './StylesControl';
import { MapStyle } from '../../../types/map';
import { PinIcon, PinPoco, PinAbb, PinPro } from '../../../assets/icons/Index';
import { useMarkers } from '../../../hooks/useMarker';
import { Fab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoMarkerModal from '../modal/InfoMarkerModal';
import DefaultMarkerModal from '../modal/DefaultMarkerModal';
import EditMarkerModal from '../modal/EditMarkerModal';
import { useUserData } from '../../../hooks/useUserData';
import { getConfig } from '../../../config/env';
import dayjs from 'dayjs';
import { ExtendedUserProfile } from '../../../interfaces/Pinners';

const MapBox = () => {
  const TOKEN = getConfig().MAPBOX_TOKEN;
  const { markers, fetchMarkers, deleteMarker } = useMarkers();
  const { userProfile } = useUserData();

  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const [newMarker, setNewMarker] = useState<MarkerData | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<MarkerData | null>(null);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isNewMarkerModal, setIsNewMarkerModal] = useState<boolean>(false);
  const [isDefaultMarkerModalOpen, setIsDefaultMarkerModalOpen] = useState<boolean>(false);
  const [isEditMarkerModalOpen, setIsEditMarkerModalOpen] = useState<boolean>(false);

  const [residencePlace, setResidencePlace] = useState<string>('');
  const [publicResidence, setPublicResidence] = useState<boolean>(false);
  const [mapStyle, setMapStyle] = useState<string>("mapbox://styles/mapbox/light-v9");
  const mapRef = useRef<MapRef>(null);

  const [residenceCoordinates, setResidenceCoordinates] = useState<{ longitude: number; latitude: number } | null>(null);
  const [initialViewState, setInitialViewState] = useState({
    longitude: 12.646361,
    latitude: 42.504154,
    zoom: 4,
  });

  const handleStyleChange = useCallback((style: MapStyle) => {
    setMapStyle(style.styleUrl);
  }, []);

  useEffect(() => {
    if (userProfile) {
      const residence = userProfile.residence;
      const isPublic = (userProfile as ExtendedUserProfile).public_residence ?? true;

      setPublicResidence(isPublic ?? false);

      if (residence && isPublic) {
        setResidencePlace(residence);

        // Geocoding per ottenere le coordinate della residenza
        fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(residence)}.json?access_token=${TOKEN}&limit=1`)
          .then(response => response.json())
          .then((geoResponse) => {
            const features = geoResponse.features;
            if (features && features.length > 0) {
              const [longitude, latitude] = features[0].center;
              setResidenceCoordinates({ longitude, latitude });
              setInitialViewState({
                longitude,
                latitude,
                zoom: 4,
              });
            } else {
              console.error('Nessun risultato trovato per la geocodifica della residenza.');
            }
          })
          .catch((err) => {
            console.error('Errore nella geocodifica:', err);
          });
      }
    }
  }, [userProfile]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (mapRef.current) {
        const mapElement = mapRef.current.getMap().getContainer();
        if (document.fullscreenElement === mapElement) {
          setIsFullscreen(true);
        } else {
          setIsFullscreen(false);
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleResidenceMarkerClick = () => {
    setIsDefaultMarkerModalOpen(true);
  };

  const handleMapClick = useCallback((event: any) => {
    const { lngLat } = event;
    setNewMarker({
      longitude: lngLat.lng,
      latitude: lngLat.lat,
      name: '',
      times_visited: 0,
      knowledge_level: 0,
      date_last_visit: dayjs().format('YYYY-MM'),
      pin_type: [],
      photo_link: '',
      content: '',
      content_private: true,
    });
    setIsNewMarkerModal(true);
    setIsModalOpen(true);
  }, []);

  const handleFabClick = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      setNewMarker({
        longitude: center.lng,
        latitude: center.lat,
        name: '',
        times_visited: 0,
        knowledge_level: 0,
        date_last_visit: dayjs().format('YYYY-MM'),
        pin_type: [],
        photo_link: '',
        content: '',
        content_private: true,
      });
      setIsNewMarkerModal(true);
      setIsModalOpen(true);
    }
  };

  const handleMarkerSubmit = useCallback(() => {
    setIsModalOpen(false);
    if (newMarker && mapRef.current) {
      mapRef.current.flyTo({
        center: [newMarker.longitude, newMarker.latitude],
        zoom: 12,
        essential: true,
      });
    }
    setNewMarker(null);
    setIsNewMarkerModal(false);
    fetchMarkers();
  }, [fetchMarkers, newMarker]);

  const handleMarkerClick = useCallback((marker: MarkerData) => {
    setSelectedMarker(marker);
    setIsNewMarkerModal(false);
    setIsModalOpen(true);
  }, []);

  const getPinComponent = (option: number) => {
    switch (option) {
      case 0:
        return PinPoco;
      case 1:
        return PinAbb;
      case 2:
        return PinIcon;
      case 3:
        return PinPro;
      default:
        return PinPoco;
    }
  };

  return (
    <>
      <Map
        ref={mapRef}
        mapboxAccessToken={TOKEN}
        initialViewState={initialViewState}
        style={{
          width: '100%',
          height: '100vh',
          maxWidth: '100%',
          maxHeight: '100vh',
          borderRadius: '1rem',
          boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
          margin: '0.5rem auto'
        }}
        mapStyle={mapStyle}
        onClick={handleMapClick}
      >
        <GeolocateControl position="top-right" />
        <FullscreenControl position="top-right" />
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 1 }}>
          <StylesControl onChange={handleStyleChange} />
        </div>

        {markers.map((marker, index) => {
          const Icon = getPinComponent(marker.knowledge_level);

          return (
            <Marker
              key={index}
              longitude={marker.longitude}
              latitude={marker.latitude}
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                handleMarkerClick(marker);
              }}
            >
              <Icon className='' />
            </Marker>
          )
        })}

        {residenceCoordinates && publicResidence && (
          <Marker
            longitude={residenceCoordinates.longitude}
            latitude={residenceCoordinates.latitude}
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              handleResidenceMarkerClick();
            }}
          >
            <PinPro className="" />
          </Marker>
        )}

        {!isFullscreen && (
          <Fab
            className='bg-primary hover:bg-primary-hover fixed right-5 md:right-10'
            aria-label='add'
            onClick={handleFabClick}
            sx={{
              position: 'absolute',
              bottom: 20,
              right: 20,
              zIndex: 1000,
            }}
          >
            <AddIcon className='text-white' />
          </Fab>
        )}
      </Map>

      {isDefaultMarkerModalOpen && (
        <DefaultMarkerModal
          open={isDefaultMarkerModalOpen}
          onClose={() => setIsDefaultMarkerModalOpen(false)}
          place={residencePlace}
        />
      )}

      {newMarker && isModalOpen && isNewMarkerModal && (
        <MarkerModal
          marker={newMarker}
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setNewMarker(null);
            setIsNewMarkerModal(false);
          }}
          onSave={handleMarkerSubmit}
          onDelete={() => {
            setIsModalOpen(false);
            setNewMarker(null);
            setIsNewMarkerModal(false);
          }}
          isNewMarker={true}
          setMarker={setNewMarker}
        />
      )}

      {!newMarker && (
        <MarkerModal
          marker={null}
          open={false}
          onClose={() => { }}
          onSave={() => { }}
          onDelete={() => { }}
          isNewMarker={false}
          setMarker={() => { }}
        />
      )}

      {selectedMarker && isModalOpen && !isNewMarkerModal && (
        <InfoMarkerModal
          marker={selectedMarker}
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedMarker(null);
          }}
          onEdit={() => {
            setIsModalOpen(false);
            setIsEditMarkerModalOpen(true);
          }}
          onDelete={async () => {
            if (selectedMarker?.id) {
              const success = await deleteMarker(selectedMarker.id);
              if (success) {
                setIsModalOpen(false);
                setSelectedMarker(null);
              }
            }
          }}
        />
      )}

      {selectedMarker && isEditMarkerModalOpen && (
        <EditMarkerModal
          marker={selectedMarker}
          open={isEditMarkerModalOpen}
          onClose={() => {
            setIsEditMarkerModalOpen(false);
            setSelectedMarker(null);
            fetchMarkers();
          }}
          onSave={() => {
            setIsEditMarkerModalOpen(false);
            setSelectedMarker(null);
            fetchMarkers();
          }}
          onDelete={async () => {
            if (selectedMarker?.id) {
              const success = await deleteMarker(selectedMarker.id);
              if (success) {
                setIsModalOpen(false);
                setSelectedMarker(null);
              }
            }
          }}
        />
      )}

    </>
  );
}

export default MapBox